/**
 * Wall assembly default values - extracted to break circular dependency
 * between wallAssembly.ts and wallFacades.ts
 */
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type {
  WallDatumAbstraction,
  WallHorizontalDatumAbstraction,
  WallVerticalDatumAbstraction,
} from "building-types/datums/datumAbstractionTypes";
import type { DatumUsage } from "building-types/datums/datumUsageTypes";
import { datumUsageNew, DatumVerticalUsageType, DatumHorizontalUsageType } from "building-types/datums/datumUsageTypes";
import type { FacadeSpanStratum } from "building-types/facades/facadeTypes";
import { SpanExtrema } from "building-types/facades/facadeTypesEnum";
import type { WallAssemblyLayerDomain } from "building-types/walls/wallAssemblyTypes";
import type { Table } from "common/data/table";

export const defaultWallAssemblyLayerDomain: WallAssemblyLayerDomain = {
  lowerDatumUsage: datumUsageNew(DatumVerticalUsageType.WALL_VERTICAL, SpanExtrema.BASE_WALL_BOTTOM),
  upperDatumUsage: datumUsageNew(DatumVerticalUsageType.WALL_VERTICAL, SpanExtrema.BASE_WALL_TOP),
  startDatumUsage: datumUsageNew(DatumHorizontalUsageType.WALL_HORIZONTAL, SpanExtrema.WALL_START),
  endDatumUsage: datumUsageNew(DatumHorizontalUsageType.WALL_HORIZONTAL, SpanExtrema.WALL_END),
};

export function wallAssemblyVerticalDatumFromFacade(
  facadeDatum: DatumAbstraction | undefined,
): WallVerticalDatumAbstraction | null {
  if (!facadeDatum) {
    return null;
  }

  if (!facadeDatum.isValidForUsage(DatumVerticalUsageType.WALL_VERTICAL)) {
    return null;
  }

  return facadeDatum;
}

function wallAssemblyHorizontalDatumFromFacade(
  facadeDatum: DatumAbstraction | undefined,
): WallHorizontalDatumAbstraction | null {
  if (!facadeDatum) {
    return null;
  }

  if (!facadeDatum.isValidForUsage(DatumHorizontalUsageType.WALL_HORIZONTAL)) {
    return null;
  }

  return facadeDatum;
}

export function wallAssemblyDatumsFromFacadeSpan(
  stratum: FacadeSpanStratum,
  facadeDatums: Table<WallDatumAbstraction>,
): {
  readonly lowerDatumUsage: DatumUsage<DatumVerticalUsageType.WALL_VERTICAL>;
  readonly upperDatumUsage: DatumUsage<DatumVerticalUsageType.WALL_VERTICAL>;
  readonly startDatumUsage: DatumUsage<DatumHorizontalUsageType.WALL_HORIZONTAL>;
  readonly endDatumUsage: DatumUsage<DatumHorizontalUsageType.WALL_HORIZONTAL>;
} {
  const {
    lowerDatumUsage: defaultLowerDatumUsage,
    upperDatumUsage: defaultUpperDatumUsage,
    startDatumUsage: defaultStartDatumUsage,
    endDatumUsage: defaultEndDatumUsage,
  } = defaultWallAssemblyLayerDomain;

  const { lowerDatumUsage, upperDatumUsage, startDatumUsage, endDatumUsage } = stratum;

  const lowerDatum = wallAssemblyVerticalDatumFromFacade(facadeDatums[lowerDatumUsage.entityId]);
  const upperDatum = wallAssemblyVerticalDatumFromFacade(facadeDatums[upperDatumUsage.entityId]);
  const startDatum = wallAssemblyHorizontalDatumFromFacade(facadeDatums[startDatumUsage.entityId]);
  const endDatum = wallAssemblyHorizontalDatumFromFacade(facadeDatums[endDatumUsage.entityId]);

  return {
    lowerDatumUsage: lowerDatum ? lowerDatumUsage : defaultLowerDatumUsage,
    upperDatumUsage: upperDatum ? upperDatumUsage : defaultUpperDatumUsage,
    startDatumUsage: startDatum ? startDatumUsage : defaultStartDatumUsage,
    endDatumUsage: endDatum ? endDatumUsage : defaultEndDatumUsage,
  };
}
